import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Loader2, Check, Camera } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Avatar } from "../../components/Avatar";
import { api } from "../../lib/api";
import { DELIVERY_ZONES, formatApiErrorDetail } from "../../lib/constants";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

function ProfilePhoto() {
  const { user, setAvatar } = useAuth();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo must be under 5MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/customer/avatar", fd);
      setAvatar(data);
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/customer/avatar");
      setAvatar(data);
      toast.success("Profile photo removed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <button type="button" data-testid="profile-photo-button" onClick={() => input.current?.click()} disabled={busy}
        className="relative w-16 h-16 rounded-full group" aria-label="Change profile photo">
        <Avatar user={user} size={64} />
        <span className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#228B22] text-white flex items-center justify-center border-2 border-white transition-transform group-hover:scale-110">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
        </span>
      </button>
      <input ref={input} data-testid="profile-photo-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} />
      {user?.avatar_url && (
        <button type="button" data-testid="profile-photo-remove" onClick={remove} disabled={busy} className="text-[11px] text-gray-400 hover:text-red-500">Remove</button>
      )}
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const { user, updateProfile, logout } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    delivery_zone: user?.delivery_zone || "Dorchester",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateProfile(form);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const field = "w-full h-12 px-4 rounded-xl bg-gray-100 focus:bg-white border border-transparent focus:border-[#228B22] outline-none transition-colors";

  return (
    <div className="max-w-lg mx-auto px-4 md:px-6 pt-6">
      <h1 className="font-display text-3xl font-bold text-[#0A1F13] tracking-tight mb-6">Profile</h1>

      <div className="flex items-center gap-4 mb-8">
        <ProfilePhoto />
        <div>
          <p data-testid="profile-name-display" className="font-semibold text-lg text-[#0A1F13]">{user?.name}</p>
          <p className="text-gray-500 text-sm">{user?.email}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <div>
          <label className="text-sm text-gray-500 mb-1.5 block">Name</label>
          <input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
        </div>
        <div>
          <label className="text-sm text-gray-500 mb-1.5 block">Email (read-only)</label>
          <input data-testid="profile-email-input" value={user?.email || ""} readOnly className={field + " text-gray-400 cursor-not-allowed"} />
        </div>
        <div>
          <label className="text-sm text-gray-500 mb-1.5 block">Phone</label>
          <input data-testid="profile-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={field} />
        </div>
        <div>
          <label className="text-sm text-gray-500 mb-1.5 block">Delivery zone</label>
          <Select value={form.delivery_zone} onValueChange={(v) => setForm({ ...form, delivery_zone: v })}>
            <SelectTrigger data-testid="profile-zone-select" className="h-12 rounded-xl bg-gray-100 border-transparent"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DELIVERY_ZONES.map((z) => (
                <SelectItem key={z.name} value={z.name}>{z.name} {z.charge > 0 ? `(+£${z.charge})` : "(Free)"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button
          data-testid="save-profile-button"
          onClick={save}
          disabled={saving}
          className="w-full h-[52px] rounded-full bg-[#228B22] hover:bg-[#1A4331] text-white font-semibold flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-60"
        >
          {saving ? <Loader2 size={20} className="animate-spin" /> : <><Check size={18} /> Save Changes</>}
        </button>
      </div>

      <button
        data-testid="logout-button"
        onClick={doLogout}
        className="w-full h-[52px] mt-4 rounded-full border border-red-200 text-red-600 font-semibold flex items-center justify-center gap-2 hover:bg-red-50 transition-colors duration-200"
      >
        <LogOut size={18} /> Logout
      </button>
      <div className="h-8" />
    </div>
  );
}
